import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isCapacitor } from '@/lib/capacitor';
import { supabase } from '@/lib/supabaseClient';
import { useSession } from '@/providers/AuthProvider';

/**
 * Initialises Capacitor push notifications.
 *
 * • Requests permission on first run
 * • Registers the device and upserts the FCM/APNs token into device_tokens
 * • Navigates to the path carried in notification.data.path on tap
 * • Cleans up all listeners on unmount / user change
 */
export function usePushNotifications() {
  const { user } = useSession();
  const navigate = useNavigate();
  const [permissionStatus, setPermissionStatus] = useState<
    'granted' | 'denied' | 'prompt' | 'not-supported'
  >('not-supported');
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Track whether we've already registered so hot-reloads don't double-register
  const registeredRef = useRef(false);

  useEffect(() => {
    if (!isCapacitor() || !user?.id) return;

    let cancelled = false;

    const init = async () => {
      setIsLoading(true);
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const { Capacitor } = await import('@capacitor/core');

        // ── Permission ────────────────────────────────────────────────────────
        const current = await PushNotifications.checkPermissions();
        let status = current.receive as 'granted' | 'denied' | 'prompt';

        if (status === 'prompt') {
          const result = await PushNotifications.requestPermissions();
          status = result.receive as 'granted' | 'denied' | 'prompt';
        }

        if (cancelled) return;
        setPermissionStatus(status);

        if (status !== 'granted') return;

        // ── Register ──────────────────────────────────────────────────────────
        if (!registeredRef.current) {
          await PushNotifications.register();
          registeredRef.current = true;
        }

        // ── Listeners ─────────────────────────────────────────────────────────
        await PushNotifications.addListener('registration', async ({ value: token }) => {
          if (cancelled) return;
          setDeviceToken(token);

          const { error } = await supabase.from('device_tokens').upsert(
            {
              user_id: user.id,
              device_token: token,
              platform: Capacitor.getPlatform(),
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'user_id,device_token' }
          );

          if (error) console.error('[PushNotif] token upsert failed:', error);
        });

        await PushNotifications.addListener('registrationError', ({ error }) => {
          console.error('[PushNotif] registration error:', error);
        });

        // Notification received while app is in foreground – no extra action needed;
        // Capacitor shows the system banner automatically (controlled by capacitor.config.json presentationOptions).
        await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('[PushNotif] received in foreground:', notification.title);
        });

        // User tapped a notification (app was background/killed)
        await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
          if (cancelled) return;
          const path: string | undefined = (notification.data as any)?.path;
          if (path) {
            navigate(path, { replace: false });
          } else {
            navigate('/vikar/vagtplan', { replace: false });
          }
        });

      } catch (err) {
        console.error('[PushNotif] init failed:', err);
        if (!cancelled) setPermissionStatus('not-supported');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    init();

    return () => {
      cancelled = true;
      // Remove all listeners to avoid duplicates on re-render / user change
      import('@capacitor/push-notifications')
        .then(({ PushNotifications }) => PushNotifications.removeAllListeners())
        .catch(() => {});
      registeredRef.current = false;
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    permissionStatus,
    deviceToken,
    isLoading,
    isSupported: isCapacitor(),
  };
}
